import {
  animate,
  state,
  style,
  transition,
  trigger,
} from "@angular/animations";
import { CommonModule } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import { TestsService } from "../../../../core/services/tests.service";
import { LoaderService } from "../../../../core/shared/services/loader.service";
import { AuthService } from "../../../../core/services/auth.service";
import { UsersService } from "../../../../core/services/users.service";
import { AuthUser } from "../../../../core/models/auth/auth.interface";
import { TEST_TYPES } from "../../../../core/shared/constants";
import ApexCharts from "apexcharts";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FormsModule } from "@angular/forms";
import { BaseComponent } from "../../../../core/shared/base.component";
import { ConfigurationService } from "../../../../core/services/configuration.service";
import { Utils } from "../../../../core/shared/utils";
import { Dimension, Test } from "../../../../core/models/test.model";
import { RouterModule, Router } from "@angular/router";

// Interfaces para la Guía de Reflexión Personal
interface ReflectionQuestion {
  id: string;
  text: string;
  type: 'text' | 'dimension-pair' | 'single-text';
  required: boolean;
  placeholder?: string;
  rows?: number;
}

interface ReflectionSection {
  id: string;
  title: string;
  description?: string;
  questions: ReflectionQuestion[];
  completed: boolean;
}

interface ReflectionAnswer {
  questionId: string;
  answer: string | { dimension: string; subdimension: string }[];
}

@Component({
  selector: "app-revision",
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: "./revision.component.html",
  styleUrl: "./revision.component.scss",
  animations: [
    trigger("fade", [
      state("visible", style({ opacity: 1 })),
      state("hidden", style({ opacity: 0, display: "none" })),
      transition("visible => hidden", [animate("500ms ease-in")]),
      transition("hidden => visible", [animate("500ms ease-out")]),
    ]),
  ],
})
export class RevisionComponent extends BaseComponent implements OnInit {
  // Inyección de dependencias
  private readonly authService: AuthService = inject(AuthService);
  private readonly testService: TestsService = inject(TestsService);
  private readonly usersService: UsersService = inject(UsersService);
  private readonly loader = inject(LoaderService);
  private readonly configService = inject(ConfigurationService);
  
  authUser: AuthUser | null = null;
  currentUser: any = null;

  // Propiedades para desplegar matrices de resultados
  expandedDimensions: Set<string> = new Set();

  // Propiedades del componente original
  tests: Test[] = [];
  testsCompleted: number = 0;
  dimensionsCompleted: number = 0;
  progressSteps: any[] = [];
  isLoadingData: boolean = true; // Nueva variable para controlar el estado de carga

  // Propiedades para la funcionalidad de PRD (ranking)
  messageHide: boolean = false;
  allPrioritiesSelected: boolean = false;
  rankingCompleted: boolean = false;
  inputsLocked: boolean = false; // Controla si los inputs están bloqueados
  isSaving: boolean = false; // Controla si se está guardando actualmente
  private priorizationDataLoaded: boolean = false;
  private initializationTimeout: any;
  highPriorityQuestions: any[] = [];
  selectOptions: number[] = Array.from({ length: 8 }, (_, i) => i + 1); // De 1 a 8
  series: any[] = [];
  chart!: ApexCharts;
  testsCompletedForPRD: boolean = false;

  selectedDimension: any = null;
  isEditingPriority: boolean = false;
  lastUsedPriority: number = 8;
  oldPriorityValues: Map<string, number> = new Map();

  // Array para controlar los valores de prioridad ya seleccionados
  usedPriorityValues: number[] = [];

  // Propiedad para alternar entre vistas
  showPRDSection: boolean = false;

  // Propiedades para la Guía de Reflexión Personal
  showReflectionGuide: boolean = false;
  reflectionSections: ReflectionSection[] = [];
  currentReflectionSection: number = 0;
  reflectionAnswers: ReflectionAnswer[] = [];
  isReflectionGuideExpanded: boolean = false;
  reflectionGuideCompleted: boolean = false;
  reflectionCompletedAt: string | null = null;

  constructor(private router: Router) {
    super();
    console.log('RevisionComponent constructor ejecutado');
    this.initializeReflectionSections();
  }

  ngOnInit(): void {
    try {
      console.log('🚀 [ngOnInit] Iniciando RevisionComponent...');
      
      // Timeout de seguridad para evitar congelamiento
      this.initializationTimeout = setTimeout(() => {
        console.warn('⚠️ [ngOnInit] Timeout de inicialización alcanzado, ocultando loader');
        this.loader.hide();
      }, 10000); // 10 segundos
      
      this.initializeComponent();
      console.log('✅ [ngOnInit] initializeComponent completado');
      
      // Cargar estado previo de la guía de reflexión si existe
      console.log('🔄 [ngOnInit] Cargando estado de reflexión...');
      this.loadReflectionGuideState();
      console.log('✅ [ngOnInit] loadReflectionGuideState completado');
      
      // Cargar prioridades guardadas
      console.log('🔄 [ngOnInit] Cargando prioridades guardadas...');
      this.loadSavedPriorities();
      console.log('✅ [ngOnInit] loadSavedPriorities completado');
      
      // Limpiar timeout cuando todo esté listo
      clearTimeout(this.initializationTimeout);
      
      // Validar el estado del ranking después de cargar todo
      setTimeout(() => {
        this.validateRankingState();
      }, 1000);
      
      console.log('🎉 [ngOnInit] RevisionComponent inicializado completamente');
    } catch (error) {
      console.error('❌ [ngOnInit] Error al inicializar RevisionComponent:', error);
      clearTimeout(this.initializationTimeout);
      this.loader.hide();
      this.error('Error al inicializar el componente. Por favor, recarga la página.');
    }
  }

  ngOnDestroy(): void {
    // Limpiar timeout al destruir el componente
    if (this.initializationTimeout) {
      clearTimeout(this.initializationTimeout);
    }
  }

  /**
   * Carga las prioridades guardadas del backend
   */
  private loadSavedPriorities(): void {
    if (!this.authUser?.id || this.priorizationDataLoaded) return;
    
    console.log('🔄 [loadSavedPriorities] Cargando prioridades guardadas...');
    this.priorizationDataLoaded = true; // Marcar como cargando para evitar duplicados
    
    this.usersService.getPriorizationData(this.authUser.id).subscribe({
      next: (response: any) => {
        console.log('✅ [loadSavedPriorities] Respuesta recibida:', response);
        const data = response?.data;
        if (data) {
          // Verificar si el ranking está completado desde data.rankingCompleted
          this.rankingCompleted = data.rankingCompleted || false;
          console.log('Estado de ranking completado (loadSavedPriorities):', this.rankingCompleted);
          
          // Bloquear inputs si el ranking está completado
          if (this.rankingCompleted) {
            this.inputsLocked = true;
          }
          
          if (data.prioridades && data.prioridades.length > 0) {
            console.log('Prioridades cargadas del backend:', data.prioridades);
            this.restorePriorities(data.prioridades);
          }
        }
      },
      error: (error: any) => {
        console.error('❌ [loadSavedPriorities] Error al cargar prioridades guardadas:', error);
        this.priorizationDataLoaded = false; // Resetear flag en caso de error
      },
      complete: () => {
        console.log('🏁 [loadSavedPriorities] Petición completada');
      }
    });
  }

  /**
   * Restaura las prioridades y rankings en las preguntas correspondientes
   */
  private restorePriorities(savedPriorities: any[]): void {
    console.log('🔄 [restorePriorities] Iniciando restauración de prioridades...');
    console.log('📊 [restorePriorities] Tests disponibles:', this.tests?.length || 0);
    console.log('📋 [restorePriorities] Prioridades a restaurar:', savedPriorities?.length || 0);
    
    if (!this.tests || this.tests.length === 0) {
      console.log('⚠️ [restorePriorities] Tests no cargados aún, las prioridades se restaurarán cuando se carguen');
      return;
    }
    
    if (!savedPriorities || savedPriorities.length === 0) {
      console.log('⚠️ [restorePriorities] No hay prioridades para restaurar');
      return;
    }

    savedPriorities.forEach((savedPriority, index) => {
      console.log(`🔍 [restorePriorities] Procesando prioridad ${index}:`, savedPriority);
      
      // Buscar la pregunta correspondiente en los tests
      let found = false;
      this.tests.forEach(test => {
        test.dimensions.forEach(dimension => {
          dimension.questions.forEach(question => {
            if (question.name === savedPriority.questionName && 
                dimension.name === savedPriority.dimensionName) {
              // Asignar la prioridad guardada directamente (sin trigger de verificación)
              (question as any).priority = savedPriority.priority;
              found = true;
              console.log(`✅ [restorePriorities] Prioridad restaurada: ${question.name} = ${savedPriority.priority}`);
              
              // Si tiene ranking, restaurarlo también
              if (savedPriority.ranking) {
                (question as any).ranking = savedPriority.ranking;
                console.log(`Prioridad y ranking restaurados para: ${question.name} = ${savedPriority.priority}, Ranking: ${savedPriority.ranking}`);
              } else {
                console.log(`Prioridad restaurada para: ${question.name} = ${savedPriority.priority}`);
              }
            }
          });
        });
      });
      
      if (!found) {
        console.warn(`⚠️ [restorePriorities] No se encontró la pregunta: ${savedPriority.questionName} en dimensión: ${savedPriority.dimensionName}`);
      }
    });
    
    // Verificar si todas las prioridades están seleccionadas después de restaurar
    this.checkAllPrioritiesSelected();
    
    // Actualizar la lista de preguntas de alta prioridad después de restaurar
    this.highPriorityQuestions = this.getHighPriorityQuestions();
    
    console.log('✅ [restorePriorities] Restauración de prioridades completada');
    console.log('📊 [restorePriorities] Preguntas de alta prioridad actualizadas:', this.highPriorityQuestions.length);
  }

  /**
   * Restaura las prioridades desde el backend después de cargar los tests
   */
  private restorePrioritiesFromBackend(): void {
    if (!this.authUser?.id || this.priorizationDataLoaded) return;
    
    console.log('🔄 [restorePrioritiesFromBackend] Iniciando restauración de prioridades...');
    this.priorizationDataLoaded = true; // Marcar como cargando para evitar duplicados
    
    this.usersService.getPriorizationData(this.authUser.id).subscribe({
      next: (response: any) => {
        console.log('✅ [restorePrioritiesFromBackend] Respuesta recibida:', response);
        const data = response?.data;
        if (data) {
          // Verificar si el ranking está completado desde data.rankingCompleted
          this.rankingCompleted = data.rankingCompleted || false;
          console.log('Estado de ranking completado:', this.rankingCompleted);
          
          // Bloquear inputs si el ranking está completado
          if (this.rankingCompleted) {
            this.inputsLocked = true;
          }
          
          if (data.prioridades && data.prioridades.length > 0) {
            console.log('Restaurando prioridades desde backend:', data.prioridades);
            this.restorePriorities(data.prioridades);
          }
        }
        // Marcar como terminada la carga de datos
        this.isLoadingData = false;
      },
      error: (error: any) => {
        console.error('❌ [restorePrioritiesFromBackend] Error al cargar prioridades para restaurar:', error);
        this.priorizationDataLoaded = false; // Resetear flag en caso de error
        this.isLoadingData = false; // Marcar como terminada incluso si hay error
        // No mostrar error al usuario, simplemente no restaurar prioridades
      },
      complete: () => {
        console.log('🏁 [restorePrioritiesFromBackend] Petición completada');
      }
    });
  }

  /**
   * Carga el estado previo de la guía de reflexión personal del backend
   */
  private loadReflectionGuideState(): void {
    if (!this.authUser?.id) {
      console.log('⚠️ [loadReflectionGuideState] No hay authUser.id, saltando carga');
      return;
    }
    
    console.log('🔄 [loadReflectionGuideState] Cargando estado de reflexión para userId:', this.authUser.id);
    this.usersService.getReflectionGuideAnswers(this.authUser.id).subscribe({
      next: (res: any) => {
        const data = res?.data;
        if (data) {
          console.log('Datos de la guía de reflexión:', data);
          
          // Validar que tanto answers como sections tengan contenido
          const hasAnswers = data.answers && Array.isArray(data.answers) && data.answers.length > 0;
          const hasSections = data.sections && Array.isArray(data.sections) && data.sections.length > 0;
          
          // Solo marcar como completado si ambos arrays tienen contenido
          this.reflectionGuideCompleted = hasAnswers && hasSections;
          this.reflectionAnswers = data.answers || [];
          // Si no hay secciones en los datos del backend, usar las secciones por defecto
          this.reflectionSections = (data.sections && data.sections.length > 0) ? data.sections : this.reflectionSections;
          this.reflectionCompletedAt = data.completedAt || null;
          
          // Mantener la guía colapsada; solo mostramos felicitaciones cuando el usuario expanda
          this.showReflectionGuide = false;
        }
      },
      error: (err) => {
        console.warn('❌ [loadReflectionGuideState] No se pudo obtener el estado de la guía de reflexión:', err);
      },
      complete: () => {
        console.log('✅ [loadReflectionGuideState] Petición completada');
      }
    });
  }

  /**
   * Inicializa las secciones de reflexión personal
   */
  private initializeReflectionSections(): void {
    this.reflectionSections = [
      {
        id: 'section-1',
        title: 'Sección 1. Observa tus resultados',
        questions: [
          {
            id: 'q1-1',
            text: '¿Qué notas al comparar tu Círculo de Vida actual con el que proyectaste a futuro?',
            type: 'text',
            required: true,
            placeholder: 'Ingrese respuesta',
            rows: 3
          },
          {
            id: 'q1-2',
            text: '¿Qué diferencias o brechas son más evidentes en cada dimensión (calidad de vida, desarrollo personal y profesional, relaciones personales, recursos para el futuro) y dentro de cada una de ellas (subdimensiones)?',
            type: 'dimension-pair',
            required: true
          }
        ],
        completed: false
      },
      {
        id: 'section-2',
        title: 'Sección 2. Conecta con lo que realmente es importante para ti',
        questions: [
          {
            id: 'q2-1',
            text: '¿Qué emociones se activan al observar tu situación actual en cada dimensión y subdimensión?',
            type: 'dimension-pair',
            required: true
          },
          {
            id: 'q2-2',
            text: '¿Qué emociones surgen al imaginarte logrando tu visión futura?',
            type: 'text',
            required: true,
            placeholder: 'Ingrese respuesta',
            rows: 3
          }
        ],
        completed: false
      },
      {
        id: 'section-3',
        title: 'Sección 3. Reconoce patrones o relaciones',
        questions: [
          {
            id: 'q3-1',
            text: '¿Observas algún patrón común entre las dimensiones o subdimensiones? (por ejemplo, ¿cómo impacta tu calidad de vida en tu desarrollo profesional o en tus relaciones?)',
            type: 'dimension-pair',
            required: true
          },
          {
            id: 'q3-2',
            text: '¿Hay alguna dimensión o subdimensión que parezca estar influyendo fuertemente en las demás?',
            type: 'dimension-pair',
            required: true
          }
        ],
        completed: false
      },
      {
        id: 'section-4',
        title: 'Sección 4. Reflexiona sobre tu trayectoria',
        questions: [
          {
            id: 'q4-1',
            text: '¿Qué decisiones, hábitos o circunstancias crees que han contribuido a tu situación actual en cada una de estas dimensiones y subdimensiones?',
            type: 'dimension-pair',
            required: true
          },
          {
            id: 'q4-2',
            text: '¿Qué momentos clave de tu vida han influido en cómo vives hoy cada una de ellas?',
            type: 'text',
            required: true,
            placeholder: 'Ingrese respuesta',
            rows: 4
          }
        ],
        completed: false
      },
      {
        id: 'section-5',
        title: 'Sección 5. Abre posibilidades',
        questions: [
          {
            id: 'q5-1',
            text: 'Si miras tus resultados con ojos de oportunidad, ¿qué ves posible para ti en cada dimensión y subdimensiones?',
            type: 'dimension-pair',
            required: true
          },
          {
            id: 'q5-2',
            text: '¿Qué nuevas preguntas te surgen sobre tu futuro al reflexionar en estas áreas?',
            type: 'text',
            required: true,
            placeholder: 'Ingrese respuesta',
            rows: 3
          }
        ],
        completed: false
      },
      {
        id: 'section-6',
        title: 'Sección 6. Cierre de la Reflexión',
        questions: [
          {
            id: 'q6-1',
            text: '¿Qué aprendizajes o comprensiones principales te llevas de esta exploración?',
            type: 'text',
            required: true,
            placeholder: 'Ingrese respuesta',
            rows: 4
          }
        ],
        completed: false
      }
    ];
  }

  /**
   * Inicializa el componente
   */
  private initializeComponent(): void {
    try {
      console.log('🔄 [initializeComponent] Inicializando componente...');
      
      const user = this.authService.getUser();
      console.log('👤 [initializeComponent] Usuario obtenido del authService:', user);
      
      this.authUser = user as unknown as AuthUser;
      this.currentUser = user; // Asignar el usuario completo
      console.log('🔐 [initializeComponent] AuthUser asignado:', this.authUser);
      console.log('👥 [initializeComponent] CurrentUser asignado:', this.currentUser);
      
      if (!this.authUser) {
        console.error('❌ [initializeComponent] No hay usuario autenticado');
        return;
      }

      // El usuario viene con _id del backend, lo mapeamos a id
      const userWithId = this.authUser as any;
      if (!this.authUser.id && userWithId._id) {
        this.authUser.id = userWithId._id;
        console.log('🆔 [initializeComponent] Mapeado _id a id:', this.authUser.id);
      }

      if (!this.authUser.id) {
        console.error('❌ [initializeComponent] AuthUser no tiene id ni _id:', this.authUser);
        return;
      }

      // Validar si el formulario de revisión ya fue completado
      const phoenixSession7Completed = (this.currentUser as any)?.stepsAnswers?.step5?.phoenixSessions6Completed;
      if (phoenixSession7Completed) {
        console.log('✅ [initializeComponent] Formulario de revisión ya completado');
        this.rankingCompleted = true;
        this.inputsLocked = true;
      }

      console.log('✅ [initializeComponent] Usuario válido, cargando tests...');
      this.loadTests();
      this.initializeProgressSteps();
      
      console.log('🎉 [initializeComponent] Componente inicializado correctamente');
    } catch (error) {
      console.error('❌ [initializeComponent] Error en initializeComponent:', error);
      this.loader.hide(); // Asegurar que el loader se oculte en caso de error
      this.error('Error al inicializar el componente. Por favor, recarga la página.');
    }
  }

  /**
   * Carga los tests del usuario
   */
  private loadTests(): void {
    if (!this.authUser || !this.authUser.id) {
      console.error('❌ [loadTests] AuthUser o authUser.id no está disponible:', this.authUser);
      this.loader.hide();
      this.isLoadingData = false;
      return;
    }

    console.log('🔄 [loadTests] Cargando tests para userId:', this.authUser.id);
    this.isLoadingData = true;
    this.loader.show();
    this.testService.find(this.authUser.id, TEST_TYPES.Autoconocimiento.id)
      .subscribe({
        next: (res: any) => {
          console.log('✅ [loadTests] Tests cargados exitosamente:', res);
          if (res.data) {
            this.tests = this.initializeTests(res.data);
            this.updatePorcentTests();
            // Restaurar prioridades después de cargar los tests
            if (!this.priorizationDataLoaded) {
              this.restorePrioritiesFromBackend();
            } else {
              // Si ya se cargaron las prioridades, marcar como terminado
              this.isLoadingData = false;
            }
            console.log('✅ [loadTests] Tests y prioridades cargados correctamente');
          } else {
            this.isLoadingData = false;
          }
          this.loader.hide();
          console.log('🏁 [loadTests] Loader ocultado');
        },
        error: (err: any) => {
          console.error('❌ [loadTests] Error al cargar tests:', err);
          this.isLoadingData = false;
          this.loader.hide();
          console.log('🏁 [loadTests] Loader ocultado por error');
        }
      });
  }

  /**
   * Inicializa los tests eliminando duplicados
   */
  private initializeTests(tests: Test[]): Test[] {
    const uniqueTests = new Map<string, Test>();
    
    tests.forEach(test => {
      const key = test._id || test.id || test.dimensions[0]?.area?.name;
      if (key && !uniqueTests.has(key)) {
        uniqueTests.set(key, test);
      }
    });
    
    return Array.from(uniqueTests.values());
  }

  /**
   * Inicializa los pasos de progreso
   */
  private initializeProgressSteps(): void {
    this.progressSteps = [
      { id: "prueba", title: "Prueba", completed: false },
      { id: "resultado", title: "Resultado", completed: false }
    ];
  }

  /**
   * Actualiza los porcentajes de los tests
   */
  updatePorcentTests(): void {
    console.log('🔄 [updatePorcentTests] Iniciando actualización de porcentajes...');
    console.log('📊 [updatePorcentTests] Tests disponibles:', this.tests?.length || 0);
    
    if (!this.tests || !Array.isArray(this.tests)) {
      console.warn('⚠️ [updatePorcentTests] Tests no está definido o no es un array:', this.tests);
      return;
    }
    
    this.testsCompleted = 0;
    this.dimensionsCompleted = 0;

    this.tests.forEach((test: Test) => {
      if (test.completed) this.testsCompleted++;
      
      let progress = 0;
      let total = 0;
      
      test.dimensions.forEach((dimension: any) => {
        if (dimension.completed) this.dimensionsCompleted++;
        
        dimension.questions.forEach((question: any) => {
          total++;
          if (this.isQuestionAnswered(question)) {
            progress++;
          }
        });
      });
      
      test.progress = Math.round((progress / total) * 100);
      test.totalProgress = total;
    });

    // Actualizar el estado de progressSteps
    const pruebaStep = this.progressSteps.find(step => step.id === "prueba");
    if (pruebaStep) {
      pruebaStep.completed = this.testsCompleted === this.tests.length && this.tests.length > 0;
    }
    
    // Actualizar la lista de preguntas de alta prioridad
    this.highPriorityQuestions = this.getHighPriorityQuestions();
    
    console.log('✅ [updatePorcentTests] Actualización completada - Tests:', this.testsCompleted, 'Dimensiones:', this.dimensionsCompleted);
    console.log('📊 [updatePorcentTests] Preguntas de alta prioridad:', this.highPriorityQuestions.length);
  }

  /**
   * Verifica si una pregunta está respondida
   */
  private isQuestionAnswered(question: any): boolean {
    return !!(question.answer && 
           question.answer.valueNow >= 1 && question.answer.valueNow <= 10 && 
           question.answer.valueFuture >= 1 && question.answer.valueFuture <= 10);
  }

  /**
   * Obtiene el número del test formateado
   */
  getTestNumber(test: Test): string {
    const testIndex = this.tests.findIndex(t => t === test);
    return String(testIndex + 1).padStart(2, '0');
  }


  /**
   * Verifica si todas las dimensiones están al 100%
   */
  areAllDimensionsCompleted(): boolean {
    return this.tests.every(test => test.progress === 100);
  }

  /**
   * Verifica si se debe mostrar la guía de reflexión personal
   */
  shouldShowReflectionGuide(): boolean {
    return this.areAllDimensionsCompleted() && this.tests.length > 0;
  }

  /**
   * Alterna la visualización de las matrices de una dimensión específica
   */
  toggleDimensionMatrices(testId: string): void {
    if (this.expandedDimensions.has(testId)) {
      this.expandedDimensions.delete(testId);
    } else {
      this.expandedDimensions.add(testId);
    }
  }

  /**
   * Verifica si una dimensión está expandida
   */
  isDimensionExpanded(testId: string): boolean {
    return this.expandedDimensions.has(testId);
  }

  /**
   * Obtiene el ID de un test de manera segura
   */
  getTestId(test: any): string {
    return test._id || test.id || '';
  }

  /**
   * Obtiene la clase CSS para el valor de brecha
   */
  getGapClass(gap: number): string {
    if (gap <= 3) return 'gap-low';
    if (gap <= 6) return 'gap-medium';
    return 'gap-high';
  }

  /**
   * Calcula la brecha de manera segura
   */
  calculateGap(question: any): number {
    const valueNow = question.answer?.valueNow || 0;
    const valueFuture = question.answer?.valueFuture || 0;
    return valueFuture - valueNow;
  }

  /**
   * Calcula el promedio del análisis presente
   */
  getAveragePresent(questions: any[]): number {
    if (!questions || questions.length === 0) return 0;
    const sum = questions.reduce((acc, q) => acc + (q.answer?.valueNow || 0), 0);
    return Math.round(sum / questions.length);
  }

  /**
   * Calcula el promedio del análisis futuro
   */
  getAverageFuture(questions: any[]): number {
    if (!questions || questions.length === 0) return 0;
    const sum = questions.reduce((acc, q) => acc + (q.answer?.valueFuture || 0), 0);
    return Math.round(sum / questions.length);
  }

  /**
   * Calcula el promedio de la brecha
   */
  getAverageGap(questions: any[]): number {
    if (!questions || questions.length === 0) return 0;
    const sum = questions.reduce((acc, q) => {
      const gap = (q.answer?.valueFuture || 0) - (q.answer?.valueNow || 0);
      return acc + gap;
    }, 0);
    return Math.round(sum / questions.length);
  }

  /**
   * Obtiene la prioridad de una pregunta específica
   */
  getQuestionPriority(question: any): string {
    return question.priority || '';
  }

  /**
   * Obtiene todas las preguntas con prioridad Alta
   */
  getHighPriorityQuestions(): any[] {
    const highPriorityQuestions: any[] = [];
    
    // Verificación de seguridad para evitar errores durante la inicialización
    if (!this.tests || !Array.isArray(this.tests)) {
      console.log('⚠️ [getHighPriorityQuestions] Tests no están disponibles aún');
      return highPriorityQuestions;
    }
    
    this.tests.forEach(test => {
      test.dimensions.forEach(dimension => {
        dimension.questions.forEach(question => {
          if (this.getQuestionPriority(question) === 'Alta') {
            highPriorityQuestions.push({
              question: question,
              dimension: dimension,
              test: test,
              gap: this.calculateGap(question)
            });
          }
        });
      });
    });
    
    return highPriorityQuestions;
  }

  /**
   * Obtiene el ranking de una pregunta específica
   */
  getQuestionRanking(question: any): string {
    return (question as any).ranking || '';
  }

  /**
   * Actualiza el ranking de una pregunta específica
   */
  updateQuestionRanking(question: any, ranking: string): void {
    // Si se está deseleccionando (ranking vacío), permitir
    if (!ranking || ranking === '') {
      (question as any).ranking = ranking;
      console.log('Ranking deseleccionado para pregunta:', question.name);
      return;
    }

    const rankingNumber = parseInt(ranking);
    
    // Verificar si el ranking ya está siendo usado por otra pregunta
    const isDuplicate = this.highPriorityQuestions.some(item => {
      if (item.question === question) return false; // Excluir la pregunta actual
      const existingRanking = this.getQuestionRanking(item.question);
      return existingRanking === ranking;
    });

    if (isDuplicate) {
      console.warn('Ranking duplicado detectado:', ranking);
      this.error(`El ranking ${ranking} ya está siendo usado por otra pregunta. Por favor, selecciona un ranking diferente.`);
      return;
    }

    // Si no es duplicado, actualizar el ranking
    (question as any).ranking = ranking;
    console.log('Ranking actualizado para pregunta:', question.name, 'Nuevo ranking:', ranking);
    
    // Verificar si todas las prioridades y rankings están completados
    this.checkAllPrioritiesSelected();
    
    // Validar el estado del ranking
    this.validateRankingState();
  }

  /**
   * Obtiene las opciones de ranking disponibles (del 1 al número de prioridades altas)
   */
  getRankingOptions(): number[] {
    const highPriorityCount = this.highPriorityQuestions.length;
    return Array.from({ length: highPriorityCount }, (_, i) => i + 1);
  }

  /**
   * Obtiene las opciones de ranking disponibles para una pregunta específica
   * (excluyendo las ya seleccionadas por otras preguntas)
   */
  getRankingOptionsForQuestion(currentQuestion: any): number[] {
    const highPriorityCount = this.highPriorityQuestions.length;
    const allOptions = Array.from({ length: highPriorityCount }, (_, i) => i + 1);
    
    // Obtener los rankings ya seleccionados por otras preguntas
    const usedRankings = new Set<number>();
    
    this.highPriorityQuestions.forEach(item => {
      if (item.question !== currentQuestion) { // Excluir la pregunta actual
        const ranking = this.getQuestionRanking(item.question);
        if (ranking && ranking !== '') {
          usedRankings.add(parseInt(ranking));
        }
      }
    });
    
    // Filtrar las opciones disponibles
    const availableOptions = allOptions.filter(option => !usedRankings.has(option));
    
    return availableOptions;
  }

  /**
   * Verifica si un ranking está duplicado
   */
  isRankingDuplicate(question: any): boolean {
    const ranking = this.getQuestionRanking(question);
    if (!ranking || ranking === '') return false;

    // Contar cuántas veces aparece este ranking
    let count = 0;
    this.highPriorityQuestions.forEach(item => {
      const itemRanking = this.getQuestionRanking(item.question);
      if (itemRanking === ranking) {
        count++;
      }
    });

    return count > 1;
  }

  /**
   * Obtiene la clase CSS para el select de ranking
   */
  getRankingSelectClass(question: any): string {
    const baseClass = 'ranking-select';
    if (this.isRankingDuplicate(question)) {
      return `${baseClass} ranking-duplicate`;
    }
    return baseClass;
  }

  /**
   * Verifica si el ranking está realmente completado
   */
  isRankingReallyCompleted(): boolean {
    if (!this.highPriorityQuestions || this.highPriorityQuestions.length === 0) {
      return true; // Si no hay preguntas de alta prioridad, considerar completado
    }

    // Verificar que todas las preguntas de alta prioridad tengan ranking
    const allRankingsCompleted = this.highPriorityQuestions.every(item => {
      const ranking = this.getQuestionRanking(item.question);
      return ranking && ranking !== '';
    });

    // Verificar que no haya rankings duplicados
    const hasDuplicates = this.highPriorityQuestions.some(item => 
      this.isRankingDuplicate(item.question)
    );

    return allRankingsCompleted && !hasDuplicates;
  }

  /**
   * Valida el estado del ranking y actualiza la propiedad rankingCompleted
   * NOTA: No actualiza rankingCompleted automáticamente para evitar ocultar el botón antes de guardar
   */
  validateRankingState(): void {
    const isReallyCompleted = this.isRankingReallyCompleted();
    
    console.log('🔍 [validateRankingState] Validando estado del ranking...');
    console.log('📊 [validateRankingState] Preguntas de alta prioridad:', this.highPriorityQuestions.length);
    console.log('✅ [validateRankingState] Ranking realmente completado:', isReallyCompleted);
    console.log('🔄 [validateRankingState] Estado actual rankingCompleted:', this.rankingCompleted);
    
    // Solo actualizar rankingCompleted si ya se guardó exitosamente en el backend
    // No actualizarlo automáticamente aquí para evitar ocultar el botón antes de guardar
  }

  /**
   * Actualiza la prioridad de una pregunta específica
   */
  updateQuestionPriority(question: any, priority: string): void {
    question.priority = priority;
    console.log('Prioridad actualizada para pregunta:', question.name, 'Nueva prioridad:', priority);
    
    // Actualizar la lista de preguntas de alta prioridad
    this.highPriorityQuestions = this.getHighPriorityQuestions();
    
    // Verificar si todas las prioridades y rankings están completados
    this.checkAllPrioritiesSelected();
    
    // Validar el estado del ranking
    this.validateRankingState();
    
    // Aquí podrías guardar en la base de datos si es necesario
    // this.saveQuestionPriority(question, priority);
  }

  /**
   * Verifica si todas las prioridades están seleccionadas y los rankings completados
   */
  checkAllPrioritiesSelected(): void {
    if (!this.tests || this.tests.length === 0) {
      this.allPrioritiesSelected = false;
      return;
    }

    let totalQuestions = 0;
    let selectedPriorities = 0;
    let highPriorityQuestions = 0;
    let completedRankings = 0;

    this.tests.forEach(test => {
      test.dimensions.forEach(dimension => {
        dimension.questions.forEach(question => {
          totalQuestions++;
          if (this.getQuestionPriority(question)) {
            selectedPriorities++;
            
            // Si es prioridad alta, verificar que tenga ranking
            if (this.getQuestionPriority(question) === 'Alta') {
              highPriorityQuestions++;
              if (this.getQuestionRanking(question) && this.getQuestionRanking(question) !== '') {
                completedRankings++;
              }
            }
          }
        });
      });
    });

    // Verificar que todas las prioridades estén seleccionadas Y todos los rankings de alta prioridad estén completados
    const allPrioritiesSelected = totalQuestions > 0 && selectedPriorities === totalQuestions;
    const allRankingsCompleted = highPriorityQuestions === 0 || completedRankings === highPriorityQuestions;
    
    this.allPrioritiesSelected = allPrioritiesSelected && allRankingsCompleted;
    
    console.log(`Prioridades seleccionadas: ${selectedPriorities}/${totalQuestions}`);
    console.log(`Rankings completados: ${completedRankings}/${highPriorityQuestions}`);
    console.log(`Todas las prioridades seleccionadas: ${allPrioritiesSelected}`);
    console.log(`Todos los rankings completados: ${allRankingsCompleted}`);
    console.log(`Final - Todo completado: ${this.allPrioritiesSelected}`);
  }

  /**
   * Guarda todas las prioridades seleccionadas y rankings
   */
  savePriorities(): void {
    console.log('Guardando prioridades y rankings...');
    
    // Bloquear inputs inmediatamente cuando se presiona Guardar/Finalizar
    this.inputsLocked = true;
    this.isSaving = true;
    
    // Recopilar todas las prioridades de todas las preguntas
    const prioritiesData: any[] = [];
    
    this.tests.forEach(test => {
      test.dimensions.forEach(dimension => {
        dimension.questions.forEach(question => {
          const priority = this.getQuestionPriority(question);
          if (priority) {
            const dataItem: any = {
              testId: test._id || test.id,
              dimensionName: dimension.name,
              questionName: question.name,
              priority: priority
            };
            
            // Si es prioridad Alta, agregar ranking
            if (priority === 'Alta') {
              dataItem.ranking = this.getQuestionRanking(question) || '';
            }
            
            prioritiesData.push(dataItem);
          }
        });
      });
    });
    
    console.log('Datos de prioridades a guardar:', prioritiesData);
    
    // Determinar si se debe marcar como completado
    const isCompleted = this.allPrioritiesSelected;
    
    // Preparar datos para guardar en user.stepsAnswers.step4
    const step4Data = {
      prioridades: prioritiesData,
      priorizacionCompleted: isCompleted,
      priorizacionEnabled: true,
      rankingCompleted: isCompleted
    };
    
    // Guardar en el backend usando el endpoint específico de priorización
    if (this.currentUser) {
      this.usersService.savePriorizationData(this.currentUser._id, step4Data).subscribe({
        next: (response: any) => {
          console.log('Datos de priorización guardados en backend (step4):', response);
          
          // Actualizar el usuario local
          if (this.currentUser) {
            if (!this.currentUser.stepsAnswers) {
              this.currentUser.stepsAnswers = {};
            }
            this.currentUser.stepsAnswers.step4 = step4Data;
          }
          
          // Solo actualizar rankingCompleted después de guardar exitosamente
          this.rankingCompleted = isCompleted;
          
          // Si el formulario se completó, marcar phoenixSessions6Completed en step5
          if (isCompleted) {
            this.markPhoenixSession7Completed();
          } else {
            this.isSaving = false;
          }
          
          // Mantener los inputs bloqueados si el ranking está completado
          // Si no está completado, desbloquear para permitir más ediciones
          if (!isCompleted) {
            this.inputsLocked = false;
          }
          
          // Mostrar mensaje de éxito apropiado
          if (isCompleted) {
            this.success('¡Priorización completada exitosamente!');
          } else {
            this.success('Priorización guardada exitosamente');
          }
        },
        error: (error: any) => {
          console.error('Error al guardar priorización en backend:', error);
          this.error('Error al guardar la priorización. Por favor, inténtalo de nuevo.');
          
          // Desbloquear inputs si hay error para permitir reintentar
          this.inputsLocked = false;
          this.isSaving = false;
        }
      });
    } else {
      // Si no hay usuario, desbloquear inputs
      this.inputsLocked = false;
      this.isSaving = false;
    }
  }

  /**
   * Marca phoenixSessions6Completed como true en step5
   */
  private markPhoenixSession7Completed(): void {
    if (!this.currentUser || !this.currentUser._id) {
      console.error('No hay usuario disponible para marcar phoenixSessions6Completed');
      this.isSaving = false;
      return;
    }

    console.log('🔄 [markPhoenixSession7Completed] Marcando phoenixSessions6Completed...');
    
    this.usersService.updateStep5(
      this.currentUser._id, 
      'phoenixSessions6Completed',
      new Date().toISOString()
    ).subscribe({
      next: (response: any) => {
        console.log('✅ [markPhoenixSession7Completed] phoenixSessions6Completed marcado exitosamente:', response);
        
        // Actualizar el usuario local
        if (this.currentUser) {
          if (!this.currentUser.stepsAnswers) {
            this.currentUser.stepsAnswers = {};
          }
          if (!this.currentUser.stepsAnswers.step5) {
            this.currentUser.stepsAnswers.step5 = {};
          }
          this.currentUser.stepsAnswers.step5.phoenixSessions6Completed = true;
          this.currentUser.stepsAnswers.step5.completedAt = new Date();
        }
        
        this.isSaving = false;
      },
      error: (error: any) => {
        console.error('❌ [markPhoenixSession7Completed] Error al marcar phoenixSessions6Completed:', error);
        // No mostrar error al usuario ya que los datos de step4 ya fueron guardados exitosamente
        this.isSaving = false;
      }
    });
  }

  /**
   * Navega a la página de objetivos
   */
  navigateToObjectives(): void {
    this.router.navigate(['/dashboard/objectives']);
  }

}